import React, { useState } from 'react'
import { ArrowRefresh2, ArrowSwitchHorizontal, Settings } from 'blaze-ui/icons'
import Styles from './TacticIntegration/TacticIntegration.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { IconButton } from 'components/IconButton/IconButton'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { Menu } from 'components/Menu/Menu'
import { Item } from '@react-stately/collections'
import { getInitials } from 'utils'
import Avatar from 'common/Avatar/Avatar'
import Silhouette from 'components/Previews/InstagramPreview/silhouette.jpg'
import X02 from 'blaze-ui/icons/16/X02'
import { useConnectedAccountCardProps } from 'components/IntegrationsModal/ManageIntegrationAccounts/hooks'
import { getIntegrationDetails } from 'components/Previews/getIntegrationDetails'
import { CollectionElement } from '@react-types/shared'
import { useModals } from 'almanac-ui/Modal'
import { DisconnectIntegrationModal } from './DisconnectIntegrationModal'
import { SwitchAccountConfirmationModal } from './SwitchAccountConfirmationModal'
import { integrationIsInBrandPlan } from './utils'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { TacticIntegrationListVariant } from './TacticIntegrationList'
import { useIntegrationChangeUpdateBrandPlan } from './hooks/useIntegrationChangeUpdateBrandPlan'
import { useSwitchAccount } from './hooks/useSwitchAccount'
import { useInitiateOauthTrigger } from 'hooks/useInitiateOauthTrigger/useInitiateOauthTrigger'

interface TacticIntegrationDropdownProps {
  integration: ExternalIntegration
  refreshColor?: string
  variant?: TacticIntegrationListVariant
  onMenuOpenChange?: (isOpen: boolean) => void
}

export const TacticIntegrationDropdown: React.FC<TacticIntegrationDropdownProps> = ({
  integration,
  refreshColor,
  variant,
  onMenuOpenChange,
}) => {
  const { name, avatarUrl, onRefreshConnection, onDisconnectAccount, showSettings, onConfigureClick } =
    useConnectedAccountCardProps(integration)
  const { openModal } = useModals()
  const { data: brandPlan } = useCurrentBrandPlan()
  const updateBrandPlanForIntegration = useIntegrationChangeUpdateBrandPlan()

  const integrationName = getIntegrationDetails(integration).username
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const handleMenuOpenChange = (open: boolean) => {
    setIsMenuOpen(open)
    onMenuOpenChange?.(open)
  }

  const { connect } = useSwitchAccount({ oldIntegration: integration })
  const { handleConnect } = useInitiateOauthTrigger({
    provider: integration.provider,
    popUp: true,
    switchFromIntegrationId: integration.id,
    onInitiatedOauth: connect,
  })

  const handleDisconnect = async (option: 'remove_channel' | 'keep_generating') => {
    if (option === 'remove_channel') {
      await updateBrandPlanForIntegration(integration.provider, 0)
    }
    onDisconnectAccount()
  }

  const handleSwitchAccount = () => {
    setIsMenuOpen(false)
    if (integrationIsInBrandPlan(integration, brandPlan)) {
      openModal(SwitchAccountConfirmationModal, {
        integration,
        onConfirm: handleConnect,
      })
    } else {
      handleConnect()
    }
  }

  const handleAction = (key: React.Key) => {
    switch (key) {
      case 'switch_account':
        handleSwitchAccount()
        return
      case 'refresh':
        setIsMenuOpen(false)
        onRefreshConnection({ popup: true })
        return
      case 'disconnect':
        setIsMenuOpen(false)
        // Only show modal if on integrations_page and the integration is in the brand plan
        if (variant === 'integrations_page' && integrationIsInBrandPlan(integration, brandPlan)) {
          openModal(DisconnectIntegrationModal, {
            integration,
            onDisconnect: handleDisconnect,
          })
        } else {
          // Onboarding or not in brand plan, disconnect directly
          onDisconnectAccount()
        }
        return
      case 'settings':
        return onConfigureClick()
    }
  }

  return (
    <MenuTrigger isOpen={isMenuOpen} onOpenChange={handleMenuOpenChange} popoverClassName={Styles.popover}>
      <IconButton data-testid="settings-button">
        <Settings size={20} color="var(--dark-80)" />
      </IconButton>
      <div>
        <div className={Styles.menuHeader}>
          <Avatar size={37} label={getInitials(name || '')} url={avatarUrl || Silhouette} />
          <div className={Styles.menuHeaderText}>
            <Text variant="smallList">{integrationName || ''}</Text>
            <Text variant="placeholder" lineClamp={1}>
              <div className={Styles.idText}>ID: {integration.provider_id}</div>
            </Text>
          </div>
        </div>
        <Menu onAction={handleAction} aria-label="Brand Kit" showCheckmark itemClassName={Styles.brandKitItem}>
          {
            [
              <Item key="switch_account" textValue="Switch account">
                <ArrowSwitchHorizontal size={20} />
                <Text variant="smallList">Switch account</Text>
              </Item>,
              integration.provider !== ExternalIntegrationEnum.WordPress && (
                <Item key="refresh" textValue="Refresh">
                  <ArrowRefresh2 size={20} color={refreshColor} />
                  <Text variant="smallList" color={refreshColor}>
                    Refresh
                  </Text>
                </Item>
              ),
              showSettings && (
                <Item key="settings" textValue="Settings">
                  <Settings size={20} />
                  <Text variant="smallList">Settings</Text>
                </Item>
              ),
              <Item key="disconnect" textValue="Disconnect">
                <X02 size={20} />
                <Text variant="smallList">Disconnect</Text>
              </Item>,
            ].filter(Boolean) as CollectionElement<HTMLElement>[]
          }
        </Menu>
      </div>
    </MenuTrigger>
  )
}
